
import { Component, Vue } from 'vue-property-decorator';
import Map from "@/components/map/Map";
import { Coordinate, Extent, IMapViewInfo, 
         IMapLayer, IMapFilter, IDMS } from "@/components/map/types";
import { User } from "@/components/User";
import { beginOfBudgetYears, sortObjectArray } from "@/common/utils";

import { MV_ANIMATION_DURATION } from "@/components/map/Map";
import { FEATURES } from "./data/map.layers";
export interface ILegendItem {
  count?: number;
  items?: IMapLayer[];
};
export interface IMapTicker {
  offset?: number;
  value?: IDMS;
};
const MAX_LEGEND_ENTRY_PER_COLUMN = 8;

@Component
export default class Print extends Vue {
  mapExtent:Extent = [95.854, 5.299, 106.064, 20.533];
  mapCenter:Coordinate = [100.5, 13.4];
  mapZoom: 10;
  basemap: string = 'osm.mapnik'; //'google.road'; //'gistda.thaichote';
  SubTitle: string = null;
  Scale:number = 5000;
  MapScale: string = '5,000';
  BudgetYears = beginOfBudgetYears();
  mapFilter: IMapFilter = null;
  mapLegends: IMapLayer[] = null;
  mapLayers: IMapLayer[] = null;
  mapLayerPoint: IMapLayer[] = [];
  mapLayerLine: IMapLayer[] = [];
  mapLayerPolygon: IMapLayer[] = [];
  LegendItems: ILegendItem[] = [{}]; //initialized with 1 element

  plant: string = '';
  $map: Map;

  get isPortrait() : boolean {
    return false; // reserved. consider to support both landscape and portrait based on ratio of map extent
  }

  // override base
  get isDoae() : boolean {
    return !!(this.User.Organization && this.User.Organization.Code.match(/^doae/i));
  }

  // override base
  get Logos() : string {
    let src = '/static/images/gisagro4.0/'; //logo_gistda_outline.png';
    src += this.isDoae ? 'logo_gistda_doae_outline.png' : 'logo-gistda.png';
    return src;
  }

  get language() : string {
    return this.$store.getters.Language;
  }

  get User() : User {
    return this.$store.getters.User;
  };

  getLegendItemWidth() : {[key:string] : any} {
    let n = Math.max(3,this.LegendItems.length);
    return {
      'flex-grow': 0,
      'width': (100 / n) + '%'
    };
  }

  replaceFeatureSource(layer:IMapLayer) {
    if (layer.mapSources) {
      layer.mapSources.forEach((ms,i) => {
        if (ms.id && ms.id.match(/^replace/)) {
          let name = ms.id.replace('replace','');
          if (!!name) {
            delete ms.id;
            layer.mapSources[i] = Object.assign(FEATURES[name], ms);
          }
        }
      });
    }
    let children = layer.children || layer.legend;
    if (children) {
      children.forEach((ml) => {
        this.replaceFeatureSource(ml);
      });
    }
  }

  async created() {
    let cache = this.$store.getters.Storage;
    if (cache) {
      this.User.HalloId = cache.uid;
      this.User.updateToken(cache.token);
      if (cache.userOrgc) {
        this.User.Organization = {
          Code: cache.userOrgc
        };
      }
      this.plant = cache.plant || '';
      this.basemap = cache.basemap || this.basemap;
      this.mapExtent = null; //cache.mapExtent;
      this.mapCenter = cache.mapCenter;
      this.mapZoom = cache.mapZoom;
      if (cache.mapLayers) {
        this.LegendItems = [{
            count: 0,
            items: []
          }];
        cache.mapLayers.forEach((ml) => {
          this.replaceFeatureSource(ml);
          ['className','style','image','icons'].forEach((p) => {
            if (ml[p]) {
              delete ml[p];
            }
          });
          let c = ml.legend || ml.children
          let newN = 1 + (c ? c.length : 0);
          let inserted:boolean = false;
          for (let i=0; i<this.LegendItems.length; i++) {
            if ((this.LegendItems[i].count + newN) <= MAX_LEGEND_ENTRY_PER_COLUMN) {
              this.LegendItems[i].count += newN;
              this.LegendItems[i].items.push(ml);
              inserted = true;
              break;
            }
          }
          if (!inserted) {
            this.LegendItems.push({
              count: newN,
              items: [ml]
            });
          }
        });
        sortObjectArray(this.LegendItems,'count desc');
        //required by print.ecoplant.vue //old version
        this.mapLegends = cache.mapLayers.map((ml) => {
          return ml;
        });
        this.mapLayers = cache.mapLayers.reverse();
      }
      this.mapFilter = cache.mapFilter;
      if (cache.language) {
        this.$store.commit('notifyChange', {
          Language: cache.language
        });
      }
    }
  }

  mounted() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  onMapCreated(map:Map) {
    this.$map = map;
    if (this.mapLayers) {
      if (this.mapFilter) {
        let filter = this.createFilter();
        this.applyFilterToLayer(filter, this.mapLayers);
        //if (this.mapFilter.ProvinceCode) {
        //  this.zoomToTambon();
        //} else {
          this.adjustScale();
        //}
      } else {
        this.adjustScale();
      }
      this.$map.createOverlayLayersAndControls(this.mapLayers);
    } else {
      this.adjustScale();
    }
  };

  onBaseMapInitialized() {
  }

  onMoveEnd() {
  }

  adjustScale() {
    setTimeout(() => {
      let s = this.$map.getNormalizedScale();
      this.Scale = this.$map.Scale = s;
      this.MapScale = s.toThousandCommaString(0);
      if (!this.SubTitle) {
        this.getSubTitle();
      }
      this.calcScaleLine();
      this.calcMapGrid();
    }, MV_ANIMATION_DURATION); // wait map to complete its zoom change
  }

  ScaleLineUnit:string = 'meter';
  ScaleLineNumber:number = 50; //mm
  ScaleLineWidth:number = 1;
  calcScaleLine() {
    let s = this.$map.Scale;
    this.ScaleLineUnit = 'meter';
    let n = (Math.floor(s)+'').length - 1;
    let pow = Math.pow(10,n);
    s = Math.round(s/pow) * pow;
    this.ScaleLineWidth = (s / this.$map.Scale);
    this.ScaleLineNumber = s / 100;
    if (this.ScaleLineNumber>=1000) {
      this.ScaleLineNumber = s / 100000;
      //this.ScaleLineWidth /= 100;
      this.ScaleLineUnit = 'kilometer'
    }
  }

  BottomLeftLat: IDMS = {
    degree: 0,
    minute: 0,
    second: 0
  };
  BottomLeftLon: IDMS = {
    degree: 0,
    minute: 0,
    second: 0
  };
  TopRightLat: IDMS = {
    degree: 0,
    minute: 0,
    second: 0
  };
  TopRightLon: IDMS = {
    degree: 0,
    minute: 0,
    second: 0
  };
  TickerTop: IMapTicker[] = [];
  TickerBottom: IMapTicker[] = [];
  TickerLeft: IMapTicker[] = [];
  TickerRight: IMapTicker[] = [];
  calcMapGrid() {
    let vinfo = this.$map.getViewInfo();
    let dhll = (vinfo.extent.wgs84[2] - vinfo.extent.wgs84[0]) / 4;
    //let dvll = vinfo.extent.wgs84[3] - vinfo.extent.wgs84[1];
    //let dhxy = vinfo.extent.utm[2] - vinfo.extent.utm[0];
    //let dvxy = vinfo.extent.utm[3] - vinfo.extent.utm[1];
    this.BottomLeftLat = this.$map.degreeToDMS(vinfo.extent.wgs84[1]);
    this.BottomLeftLon = this.$map.degreeToDMS(vinfo.extent.wgs84[0]);
    this.TopRightLat = this.$map.degreeToDMS(vinfo.extent.wgs84[3]);
    this.TopRightLon = this.$map.degreeToDMS(vinfo.extent.wgs84[2]);
    let lon, lonMax;
    let lat, latMax;
    let pixel;
    this.TickerTop = [];
    this.TickerBottom = [];
    this.TickerLeft = [];
    this.TickerRight = [];
    if (dhll>1) {
      dhll = Math.round(dhll);
      lon = this.BottomLeftLon.degree + dhll;
      lonMax = this.TopRightLon.degree;
      lat = this.BottomLeftLat.degree + dhll;
      latMax = this.TopRightLat.degree;
    } else {
      dhll *= 60;
      if (dhll>1) {
        dhll = Math.round(dhll) / 60;
        lon = this.BottomLeftLon.degree + (this.BottomLeftLon.minute / 60) + dhll;
        lonMax = this.TopRightLon.degree + (this.TopRightLon.minute / 60);
        lat = this.BottomLeftLat.degree + (this.BottomLeftLat.minute / 60) + dhll;
        latMax = this.TopRightLat.degree + (this.TopRightLat.minute / 60);
      } else {
        dhll *= 60;
        if (dhll>1) {
          dhll = Math.round(dhll) / 3600;
          lon = this.BottomLeftLon.degree + 
                (this.BottomLeftLon.minute / 60) +
                (this.BottomLeftLon.second / 3600) + dhll;
          lonMax = this.TopRightLon.degree + 
                (this.TopRightLon.minute / 60) +
                (this.TopRightLon.second / 3600);
          lat = this.BottomLeftLat.degree + 
                (this.BottomLeftLat.minute / 60) +
                (this.BottomLeftLat.second / 3600) + dhll;
          latMax = this.TopRightLat.degree + 
                (this.TopRightLat.minute / 60) +
                (this.TopRightLat.second / 3660);
        } else {
          return;
        }
      }
    }
    this.TickerTop = this.calcMapTickerH(lon,lonMax,dhll,vinfo.extent.wgs84[3]);
    this.TickerBottom = this.calcMapTickerH(lon,lonMax,dhll,vinfo.extent.wgs84[1]);
    this.TickerLeft = this.calcMapTickerV(lat,latMax,dhll,vinfo.extent.wgs84[0]);
    this.TickerRight = this.calcMapTickerV(lat,latMax,dhll,vinfo.extent.wgs84[2]);
  }

  calcMapTickerH(min,max,delta,lat) {
    let ticker:IMapTicker[] = [];
    let px = this.$map.getPixelFromLonLat([max,lat]);
    let wx = px[0];
    let dms = this.$map.degreeToDMS(min);
    let d = dms.degree + (dms.minute / 60) + (dms.second / 3600);
    px = this.$map.getPixelFromLonLat([d,lat]);
    while ((d<max) && (wx-px[0]>=110)) {
      ticker.push({
        offset: px[0],
        value: dms
      });
      d += delta;
      dms = this.$map.degreeToDMS(d);
      d = dms.degree + (dms.minute / 60) + (dms.second / 3600);
      px = this.$map.getPixelFromLonLat([d,lat]);
    }
    return ticker;
  };

  calcMapTickerV(min,max,delta,lon) {
    let ticker:IMapTicker[] = [];
    let dms = this.$map.degreeToDMS(min);
    let d = dms.degree + (dms.minute / 60) + (dms.second / 3600);
    let px = this.$map.getPixelFromLonLat([lon,d]);
    while (d<max && px[1]>=110) {
      ticker.push({
        offset: px[1],
        value: dms
      });
      d += delta;
      dms = this.$map.degreeToDMS(d);
      d = dms.degree + (dms.minute / 60) + (dms.second / 3600);
      px = this.$map.getPixelFromLonLat([lon,d]);
    }
    return ticker;
  };
  
  createFilter() : {[key:string] : string} {
    let filter:any = {};
    if (this.mapFilter.TambonCode) {
      filter.aag = 'aag5_fk=' + this.mapFilter.TambonCode;
    } else if (this.mapFilter.AmphoeCode) {
      filter.aag = 'aag4_fk=' + this.mapFilter.AmphoeCode;
    } else if (this.mapFilter.ProvinceCode) {
      filter.aag = 'aag3_fk=' + this.mapFilter.ProvinceCode;
    }
    if (this.mapFilter.CropSpecies) {
      filter.crop = 'crop_fk=' + this.mapFilter.CropSpecies;
    } else if (this.mapFilter.CropGenus) {
      filter.crop = 'genus_fk=' + this.mapFilter.CropGenus;
    } else if (this.mapFilter.CropType) {
      filter.crop = 'type_fk=' + this.mapFilter.CropType;
    }
    if (this.mapFilter.DeedType) {
      filter.deed = "deed_type='" + this.mapFilter.DeedType + "'";
    }
    if (this.mapFilter.GrowOrHarvest && this.mapFilter.GrowOrHarvestYear) {
      let year = this.mapFilter.GrowOrHarvestYear;
      if (year>2500) year -= 543;
      filter.year = this.mapFilter.GrowOrHarvest + '_yyyy=' + year;
    }
    if (this.mapFilter.AreaType=='lte' || this.mapFilter.AreaType=='gte') {
      if (!!this.mapFilter.Area1.Rai || this.mapFilter.Area1.Rai===0 ||
          !!this.mapFilter.Area1.Ngan || this.mapFilter.Area1.Ngan===0 ||
          !!this.mapFilter.Area1.SqWah || this.mapFilter.Area1.SqWah===0)  {
        let sqm = (this.mapFilter.Area1.Rai || 0) * 1600 +
                  (this.mapFilter.Area1.Ngan || 0) * 400 +
                  (this.mapFilter.Area1.SqWah || 0) * 4;
        filter.area = 'area_sqm' + (this.mapFilter.AreaType=='lte' ? '<=' : '>=') + sqm;
      }
    } else if (this.mapFilter.AreaType=='between' && 
      (!!this.mapFilter.Area1.Rai || this.mapFilter.Area1.Rai===0 ||
        !!this.mapFilter.Area1.Ngan || this.mapFilter.Area1.Ngan===0 ||
        !!this.mapFilter.Area1.SqWah || this.mapFilter.Area1.SqWah===0) && 
      (!!this.mapFilter.Area2.Rai || this.mapFilter.Area2.Rai===0 ||
        !!this.mapFilter.Area2.Ngan || this.mapFilter.Area2.Ngan===0 ||
        !!this.mapFilter.Area2.SqWah || this.mapFilter.Area2.SqWah===0)) {
      let sqm1 = (this.mapFilter.Area1.Rai || 0) * 1600 +
                  (this.mapFilter.Area1.Ngan || 0) * 400 +
                  (this.mapFilter.Area1.SqWah || 0) * 4;
      let sqm2 = (this.mapFilter.Area2.Rai || 0) * 1600 +
                  (this.mapFilter.Area2.Ngan || 0) * 400 +
                  (this.mapFilter.Area2.SqWah || 0) * 4;
      if (sqm1<sqm2) {
        filter.area = 'area_sqm BETWEEN ' + sqm1 + ' AND ' + sqm2;
      } else if (sqm1>sqm2) {
        filter.area = 'area_sqm BETWEEN ' + sqm2 + ' AND ' + sqm1;
      } else {
        filter.area = 'area_sqm=' + sqm1;
      }
    }
    return filter;
  }
  
  applyFilterToLayer(filter:{[key:string] : string}, layers:IMapLayer[], parent?:IMapLayer) {
    layers.forEach((ml) => {
      if (ml.mapSources && ml.mapSources.length>0) {
        let mSources = [];
        ml.mapSources.forEach((ms) => {
          let i, inParent = false;
          if (parent && parent.mapSources) {
            for (i=0; i<parent.mapSources.length && !inParent; i++) {
              let pms = parent.mapSources[i];
              inParent = (pms.url==ms.url) && pms.params && ms.params && 
                         (pms.params.LAYERS==ms.params.LAYERS);
            }
          }
          if (!inParent) {
            if (ms.filterOptions && ms.filterOptions.length>0) {
              let cqls:string[] = [];
              for (let fo in filter) {
                if (ms.filterOptions.indexOf(fo)>=0) {
                  cqls.push(filter[fo]);
                }
              }
              if (cqls.length>0) {
                let cql = cqls.join(' AND ');
                ms.params = Object.assign({},ms.params,{CQL_FILTER:cql});
              }
            }
            mSources.push(ms);
          }
        });
        if (mSources.length==0) {
          ['mapSources', 'type', 'checked', 'radioGroup', 'visible'].forEach((p) => {
            if (ml[p] != undefined) {
              delete ml[p];
            }
          });
        }
      }
      let children = ml.children || ml.legend;
      if (children) {
        this.applyFilterToLayer(filter,children,ml);
      }
    });
  }
  
  async zoomToProvince() {
    try {
      if (!this.mapFilter.ProvinceCode) {
        this.$map.clearAoi();
        this.$map.MapExtent = this.mapExtent;
      } else {
        let url = 'gis/province/' + this.mapFilter.ProvinceCode + '?crs=3857';
        let feature  = await this.User.requestResource(url);
        let pn = feature.properties.province + '';
        this.SubTitle = 'บริเวณพื้นที่';
        if (this.mapFilter.ProvinceCode==10) {
          this.SubTitle += pn;
        } else {
          if (!!(pn.match(/^จ\./))) {
            this.SubTitle += 'จังหวัด' + pn.substr(2);
          } else if (!!(pn.match(/^จังหวัด/))) {
            this.SubTitle += pn;
          } else {
            this.SubTitle += 'จังหวัด' + pn;
          }
        }
        feature = this.$map.GeoJSONToFeatures(feature);
        this.$map.addFeaturesToLayer(feature, "AoiLayer", {
          zoomTo: true
        });
        this.adjustScale();
      }
    } catch (error) {}
  }

  async zoomToAmphoe() {
    try {
      if (!this.mapFilter.AmphoeCode) {
        this.zoomToProvince();
      } else {
        let url = 'gis/amphoe/' + this.mapFilter.AmphoeCode + '?crs=3857';
        let feature = await this.User.requestResource(url);
        let pn = feature.properties.province + '';
        let an = feature.properties.amphoe + '';
        this.SubTitle = 'บริเวณพื้นที่';
        if (this.mapFilter.ProvinceCode==10) {
          this.SubTitle += (an + ' ' + pn);
        } else {
          if (!!(an.match(/^อ\./))) {
            this.SubTitle += 'อำเภอ' + an.substr(2);
          } else if (!!(an.match(/^อำเภอ/))) {
            this.SubTitle += an;
          } else {
            this.SubTitle += 'อำเภอ' + an;
          }
          if (!!(pn.match(/^จ\./))) {
            this.SubTitle += ' จังหวัด' + pn.substr(2);
          } else if (!!(pn.match(/^จังหวัด/))) {
            this.SubTitle += ' ' + pn;
          } else {
            this.SubTitle += ' จังหวัด' + pn;
          }
        }
        feature = this.$map.GeoJSONToFeatures(feature);
        this.$map.addFeaturesToLayer(feature, "AoiLayer", {
          zoomTo: true
        });
        this.adjustScale();
      }
    } catch (error) {}
  }

  async zoomToTambon() {
    try {
      if (!this.mapFilter.TambonCode) {
        this.zoomToAmphoe();
      } else {
        let url = 'gis/tambon/' + this.mapFilter.TambonCode + '?crs=3857';
        let feature = await this.User.requestResource(url);
        let pn = feature.properties.province + '';
        let an = feature.properties.amphoe + '';
        let tn = feature.properties.tambon + '';
        this.SubTitle = 'บริเวณพื้นที่';
        if (this.mapFilter.ProvinceCode==10) {
          this.SubTitle += (tn + ' ' + an + ' ' + pn);
        } else {
          if (!!(tn.match(/^ต\./))) {
            this.SubTitle += 'ตำบล' + tn.substr(2);
          } else if (!!(tn.match(/^ตำบล/))) {
            this.SubTitle += tn;
          } else {
            this.SubTitle += 'ตำบล' + tn;
          }
          if (!!(an.match(/^อ\./))) {
            this.SubTitle += ' อำเภอ' + an.substr(2);
          } else if (!!(an.match(/^อำเภอ/))) {
            this.SubTitle += ' ' + an;
          } else {
            this.SubTitle += ' อำเภอ' + an;
          }
          if (!!(pn.match(/^จ\./))) {
            this.SubTitle += ' จังหวัด' + pn.substr(2);
          } else if (!!(pn.match(/^จังหวัด/))) {
            this.SubTitle += ' ' + pn;
          } else {
            this.SubTitle += ' จังหวัด' + pn;
          }
        }
        feature = this.$map.GeoJSONToFeatures(feature);
        this.$map.addFeaturesToLayer(feature, "AoiLayer", {
          zoomTo: true
        });
        this.adjustScale();
      }
    } catch (error) {}
  }
  
  async getSubTitle() {
    let vc = this.$map.getViewInfo().center;
    let lat = vc.wgs84[1];
    let lon = vc.wgs84[0];
    let url = 'gis/tambon/' + lon + ',' + lat + ',0';
    try {
      let aa = await this.User.requestResource(url);
      this.SubTitle = (this.mapFilter && this.mapFilter.ProvinceCode) ?
                      '' : 'บริเวณพื้นที่';
      if (aa.properties.aag3_fk==10) {
        this.SubTitle += (!!aa.properties.tambon.match(/^แขวง/) ? '' : 'แขวง') + aa.properties.tambon + ' ' +
                         (!!aa.properties.amphoe.match(/^เขต/) ? '' : 'เขต') + aa.properties.amphoe + ' ' +
                         aa.properties.province;
      } else {
        if (!!(aa.properties.tambon.match(/^ต\./))) {
          this.SubTitle += 'ตำบล' + aa.properties.tambon.substr(2);
        } else if (!!(aa.properties.tambon.match(/^ตำบล/))) {
          this.SubTitle += aa.properties.tambon;
        } else {
          this.SubTitle += 'ตำบล' + aa.properties.tambon;
        }
        if (!!(aa.properties.amphoe.match(/^อ\./))) {
          this.SubTitle += ' อำเภอ' + aa.properties.amphoe.substr(2);
        } else if (!!(aa.properties.amphoe.match(/^อำเภอ/))) {
          this.SubTitle += ' ' + aa.properties.amphoe;
        } else {
          this.SubTitle += ' อำเภอ' + aa.properties.amphoe;
        }
        if (!!(aa.properties.province.match(/^จ\./))) {
          this.SubTitle += ' จังหวัด' + aa.properties.province.substr(2);
        } else if (!!(aa.properties.province.match(/^จังหวัด/))) {
          this.SubTitle += ' ' + aa.properties.province;
        } else {
          this.SubTitle += ' จังหวัด' + aa.properties.province;
        }
      }
    } catch (error) {
      let NS = lat>=0? 'N' : 'S';
      let EW = lon>=0 ? 'E' : 'W';
      this.SubTitle = 'พิกัดจุดกลางแผนที่ ';
      this.SubTitle += lat.toThousandCommaString(6) + NS + ', ' + lon.toThousandCommaString(6) + EW;
      //this.SubTitle += ' UTM: ' + vc.utm.x.toThousandCommaString(2) + EW + ', ' + vc.utm.y.toThousandCommaString(2) + NS + ' Zone ' + vc.utm.zone;
    }
  }
}